import MediaDomain from "@/domains/communityTalk/mediaDomain";
import MediaUtil from "@/modules/utils/mediaUtil";
import { PostMediaViewModel } from "@/viewModels/communityTalk/postMediaViewModel";

/**
 * 画像・動画投稿処理。
 * UI層 <-> Controller層[ｺｺ] <-> Domain層
 */
export default class MediaController {
	private readonly _mediaDomain: MediaDomain;

	/**
	 * コンストラクタ。
	 */
	constructor() {
		this._mediaDomain = new MediaDomain();
	}

	/**
	 * 画像の最大投稿可能数。
	 */
	public maxImageCount = 5;
	/**
	 * 動画の最大投稿可能数。
	 */
	public maxMovieCount = 1;
	/**
	 *  画像の最大サイズ(メガバイト)。
	 */
	public pictureMaxSizeByte = 10;
	/**
	 *  動画の最大サイズ(メガバイト)。
	 */
	public MovieMaxSizeByte = 500;
	/**
	 * 投稿画像・動画のバリデーションチェックを行う。
	 * @param file チェック対象の画像・動画ファイル。
	 * @return バリデーションエラーメッセージ。バリデーションエラーでなければ空配列が返却。
	 */
	public async checkPictureAndMovie(files: File[]): Promise<string[]> {
		const errorMessage: string[] = [];
		let imageCount = 0;
		let movieCount = 0;

		for (let i = 0; i < files.length; i++) {
			if (!MediaUtil.isValidExtensionImageFile(files[i])) {
				if (!MediaUtil.isValidExtensionMovieFile(files[i])) {
					errorMessage.push(`画像の拡張子は.jpg, .jpeg, .png, .gifのもののみを選んでください。`);
				} else {
					// 動画ファイル
					movieCount++;
					if (!MediaUtil.isValidFileSize(files[i], "movie")) {
						errorMessage.push(`ファイルサイズが${this.MovieMaxSizeByte}MB以下のものを選んでください。選択動画のファイルサイズ: ${Math.round((files[i].size / 1024 / 1024) * 100) / 100}MB`);
					}
					const isValidMoviePlayTimeLength = await MediaUtil.isValidMoviePlayTimeLength(files[i]);
					if (!isValidMoviePlayTimeLength.isValid) {
						errorMessage.push(
							`動画の再生時間が${MediaUtil.maxMoviePlayTime}秒以下のものを選んでください。選択動画の再生時間: ${Math.round(isValidMoviePlayTimeLength.playTime * 100) / 100}秒`
						);
					}
				}
			} else {
				// 画像ファイル
				imageCount++;
				if (!MediaUtil.isValidFileSize(files[i], "image")) {
					errorMessage.push(`ファイルサイズが${this.pictureMaxSizeByte}MB以下のものを選んでください。選択画像のファイルサイズ: ${Math.round((files[i].size / 1024 / 1024) * 100) / 100}MB`);
				}
			}
		}
		if (imageCount > this.maxImageCount) {
			errorMessage.push(`画像は${this.maxImageCount}枚まで投稿可能です(選択された画像数:${imageCount}枚)。`);
		}
		if (movieCount > this.maxMovieCount) {
			errorMessage.push(`動画は${this.maxMovieCount}枚まで投稿可能です(選択された動画数:${movieCount}枚)。`);
		}
		return errorMessage;
	}

	/**
	 * 画像・動画を投稿。
	 */
	public async createMedias(files: File[], tenantId?: string): Promise<PostMediaViewModel[]> {
		// Null許容不可能な値がNullである場合、Error
		if (!tenantId) {
			throw new Error("tenantId: value not exist.");
		}
		const result: PostMediaViewModel[] = [];

		let movieCount = 0;
		for (let i = 0; i < files.length; i++) {
			const mediaType = MediaUtil.isValidExtensionImageFile(files[i]) ? "Image" : "Movie";
			try {
				if (mediaType === "Image") {
					// 画像
					const pictureResult = await this._mediaDomain.createPictureData(tenantId, files[i]);
					result.push({
						mediaIdTmp: pictureResult.tmpId,
						mediaUrl: pictureResult.url,
						type: "Image",
						movieThumbnailIndex: null,
					});
				} else {
					// 動画
					movieCount++;
					const movieResult = await this._mediaDomain.createMovieData(tenantId, files[i], movieCount);
					result.push({
						mediaIdTmp: movieResult.tmpId,
						mediaUrl: movieResult.thumbnailUrl,
						type: "Movie",
						movieThumbnailIndex: movieResult.thumbnailIndex,
					});
				}
			} catch (e) {
				throw new Error(`${i + 1}個目の${mediaType === "Image" ? "画像" : "動画"}にてアップロードに失敗しました。`);
			}
		}

		// 動画を先頭に配置するため要素を入れ替え
		return [...result.filter((e) => e.type === "Movie"), ...result.filter((e) => e.type === "Image")];
	}

	/**
	 * プロフィール画像のバリデーションチェックを行う。
	 * @param file チェック対象の画像ファイル。
	 * @return バリデーションエラーメッセージ。バリデーションエラーでなければ空配列が返却。
	 */
	public checkProfilePicture(file: File): string[] {
		const errorMessage: string[] = [];
		if (!MediaUtil.isValidExtensionImageFile(file)) {
			errorMessage.push("画像の拡張子は.jpg, .jpeg, .png, .gifのもののみを選んでください。");
		}
		if (!MediaUtil.isValidFileSize(file, "profilePicture")) {
			errorMessage.push(`ファイルサイズが${this.pictureMaxSizeByte}MB以下のものを選んでください。選択画像のファイルサイズ: ${Math.round((file.size / 1024 / 1024) * 100) / 100}MB`);
		}
		return errorMessage;
	}

	/**
	 * プロフィール画像を投稿。
	 */
	public async createProfilePicture(file: File, tenantId?: string): Promise<{ tmpId: string; url: string }> {
		// Null許容不可能な値がNullである場合、Error
		if (!tenantId) {
			throw new Error("tenantId: value not exist.");
		}

		return await this._mediaDomain.createProfilePictureData(tenantId, file);
	}
}
